import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  Copy,
  Edit3,
  Loader2,
  Plus,
  Trash2,
  X
} from 'lucide-react';

const CONDITION_FIELDS = [
  { value: 'tipo_operacao', label: 'Tipo de operação' },
  { value: 'emitente_cnpj', label: 'Emitente (CNPJ)' },
  { value: 'destinatario_cnpj', label: 'Destinatário (CNPJ)' },
  { value: 'valor_total', label: 'Valor total' },
  { value: 'cfop', label: 'CFOP' },
  { value: 'natureza_operacao', label: 'Natureza da operação' }
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'igual a' },
  { value: 'not_equals', label: 'diferente de' },
  { value: 'contains', label: 'contém' },
  { value: 'starts_with', label: 'começa com' },
  { value: 'ends_with', label: 'termina com' },
  { value: 'greater_than', label: 'maior que' },
  { value: 'less_than', label: 'menor que' }
];

const ACTION_TYPES = [
  { value: 'set_due_date_offset', label: 'Ajustar vencimento (dias)' },
  { value: 'set_financial_type', label: 'Definir tipo financeiro' },
  { value: 'set_gera_financeiro', label: 'Ativar/desativar gera_financeiro' }
];

const FINANCIAL_TYPES = [
  { value: 'automatico', label: 'Automático' },
  { value: 'pagar', label: 'Conta a pagar' },
  { value: 'receber', label: 'Conta a receber' }
];

const DUE_DATE_BASE_OPTIONS = [
  { value: 'auto', label: 'Detectar automaticamente' },
  { value: 'due_date', label: 'Data de vencimento atual' },
  { value: 'issue_date', label: 'Data de emissão' },
  { value: 'current_date', label: 'Data atual' }
];

const NUMERIC_FIELDS = new Set(['valor_total']);
const NUMERIC_OPERATORS = new Set(['greater_than', 'less_than']);

const DEFAULT_CONDITION = {
  field: 'tipo_operacao',
  operator: 'equals',
  value: ''
};

const DEFAULT_ACTION = {
  type: 'set_due_date_offset',
  params: { days: 0, base: 'auto' }
};

const buildEmptyForm = () => ({
  nome: '',
  prioridade: 0,
  is_active: true,
  conditions: [{ ...DEFAULT_CONDITION }],
  actions: [{ ...DEFAULT_ACTION }]
});

const normalizeConditionForForm = (condition = {}) => ({
  field: condition.field || 'tipo_operacao',
  operator: condition.operator || 'equals',
  value:
    condition.value === undefined || condition.value === null
      ? ''
      : typeof condition.value === 'number'
      ? String(condition.value)
      : String(condition.value)
});

const normalizeActionForForm = (action = {}) => {
  const type = action.type || 'set_due_date_offset';
  const params = action.params && typeof action.params === 'object' ? action.params : {};

  if (type === 'set_due_date_offset') {
    return {
      type,
      params: {
        days: Number.isFinite(Number(params.days)) ? Number(params.days) : 0,
        base: typeof params.base === 'string' && params.base ? params.base : 'auto'
      }
    };
  }

  if (type === 'set_financial_type') {
    const financialType = params.financialType || params.tipo || 'automatico';
    return {
      type,
      params: { financialType: String(financialType || 'automatico') }
    };
  }

  if (type === 'set_gera_financeiro') {
    const enabledRaw =
      params.enabled ?? params.ativo ?? params.value ?? params.toggle ?? true;
    const enabled = ['false', '0', 0, false, 'nao', 'não', 'off'].includes(
      String(enabledRaw).toLowerCase()
    )
      ? false
      : Boolean(enabledRaw ?? true);
    return {
      type,
      params: { enabled }
    };
  }

  return {
    type,
    params: { ...params }
  };
};

const buildFormFromRule = (rule) => {
  if (!rule) return buildEmptyForm();

  const base = buildEmptyForm();
  base.nome = rule.nome || '';
  base.prioridade = Number.isFinite(Number(rule.prioridade)) ? Number(rule.prioridade) : 0;
  base.is_active = rule.is_active !== false;

  const conditions = Array.isArray(rule.conditions) && rule.conditions.length > 0
    ? rule.conditions.map(normalizeConditionForForm)
    : [{ ...DEFAULT_CONDITION }];

  const actions = Array.isArray(rule.actions) && rule.actions.length > 0
    ? rule.actions.map(normalizeActionForForm)
    : [{ ...DEFAULT_ACTION }];

  base.conditions = conditions;
  base.actions = actions;

  return base;
};

const shouldUseNumericInput = (condition) => {
  if (!condition) return false;
  if (NUMERIC_FIELDS.has(condition.field)) return true;
  if (NUMERIC_OPERATORS.has(condition.operator)) return true;
  return false;
};

const parseConditionValueForSubmit = (condition) => {
  if (!condition) return null;
  const numeric = shouldUseNumericInput(condition);
  if (!numeric) {
    return condition.value;
  }
  const numberValue = Number(condition.value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const buildPayloadFromForm = (formState) => ({
  nome: formState.nome.trim(),
  prioridade: Number(formState.prioridade) || 0,
  is_active: Boolean(formState.is_active),
  conditions: formState.conditions.map((condition) => ({
    field: condition.field,
    operator: condition.operator,
    value: parseConditionValueForSubmit(condition)
  })),
  actions: formState.actions.map((action) => {
    if (action.type === 'set_due_date_offset') {
      return {
        type: action.type,
        params: {
          days: Number(action.params?.days) || 0,
          base: action.params?.base || 'auto'
        }
      };
    }
    if (action.type === 'set_financial_type') {
      return {
        type: action.type,
        params: {
          financialType: action.params?.financialType || 'automatico'
        }
      };
    }
    if (action.type === 'set_gera_financeiro') {
      return {
        type: action.type,
        params: { enabled: Boolean(action.params?.enabled) }
      };
    }
    return {
      type: action.type,
      params: { ...(action.params || {}) }
    };
  })
});

const validateForm = (formState) => {
  if (!formState.nome || !formState.nome.trim()) {
    return 'Informe um nome para a regra.';
  }

  if (!Array.isArray(formState.conditions) || formState.conditions.length === 0) {
    return 'Adicione ao menos uma condição.';
  }

  for (const condition of formState.conditions) {
    if (!condition.field || !condition.operator) {
      return 'Preencha campo e operador em todas as condições.';
    }
    if (condition.value === '' || condition.value === null || condition.value === undefined) {
      return 'Defina um valor para todas as condições.';
    }
  }

  if (!Array.isArray(formState.actions) || formState.actions.length === 0) {
    return 'Adicione ao menos uma ação.';
  }

  for (const action of formState.actions) {
    if (!action.type) {
      return 'Selecione um tipo de ação.';
    }
    if (action.type === 'set_due_date_offset') {
      const dias = Number(action.params?.days);
      if (!Number.isFinite(dias)) {
        return 'Informe um número válido de dias para o ajuste de vencimento.';
      }
    }
    if (action.type === 'set_financial_type' && !action.params?.financialType) {
      return 'Escolha o tipo financeiro na ação correspondente.';
    }
  }

  return null;
};

export default function ImportRuleConfigurator({
  isOpen,
  onClose,
  rules = [],
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
  onDuplicateRule,
  loading = false,
  isSubmitting = false,
  busyRuleId = null
}) {
  const [view, setView] = useState('list');
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [formState, setFormState] = useState(buildEmptyForm());
  const [formError, setFormError] = useState('');
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [localBusyId, setLocalBusyId] = useState(null);

  const effectiveSubmitting = localSubmitting || isSubmitting;
  const effectiveBusyId = busyRuleId ?? localBusyId;

  useEffect(() => {
    if (!isOpen) {
      setView('list');
      setEditingRuleId(null);
      setFormState(buildEmptyForm());
      setFormError('');
      setLocalSubmitting(false);
      setLocalBusyId(null);
    }
  }, [isOpen]);

  const sortedRules = useMemo(() => {
    if (!Array.isArray(rules)) return [];
    return [...rules].sort((a, b) => {
      const priorityDiff = (b?.prioridade || 0) - (a?.prioridade || 0);
      if (priorityDiff !== 0) return priorityDiff;
      return String(a?.nome || '').localeCompare(String(b?.nome || ''));
    });
  }, [rules]);

  if (!isOpen) {
    return null;
  }

  const handleStartCreate = () => {
    setView('edit');
    setEditingRuleId(null);
    setFormState(buildEmptyForm());
    setFormError('');
  };

  const handleStartEdit = (rule) => {
    setView('edit');
    setEditingRuleId(rule?.id ?? null);
    setFormState(buildFormFromRule(rule));
    setFormError('');
  };

  const handleDuplicate = async (rule) => {
    if (!onDuplicateRule) return;
    setLocalBusyId(rule.id);
    try {
      const result = await onDuplicateRule(rule);
      if (!result) {
        setLocalBusyId(null);
        return;
      }
    } catch (error) {
      console.error('Erro ao duplicar regra de importação:', error);
    }
    setLocalBusyId(null);
  };

  const handleDelete = async (rule) => {
    if (!onDeleteRule) return;
    const confirmed = window.confirm(`Deseja realmente excluir a regra "${rule.nome}"?`);
    if (!confirmed) return;
    setLocalBusyId(rule.id);
    try {
      const result = await onDeleteRule(rule.id);
      if (!result) {
        setLocalBusyId(null);
        return;
      }
    } catch (error) {
      console.error('Erro ao excluir regra de importação:', error);
      setLocalBusyId(null);
      return;
    }
    setLocalBusyId(null);
  };

  const handleCancelEdit = () => {
    setView('list');
    setEditingRuleId(null);
    setFormState(buildEmptyForm());
    setFormError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!onCreateRule && !onUpdateRule) {
      return;
    }

    const validationError = validateForm(formState);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const payload = buildPayloadFromForm(formState);
    setFormError('');
    setLocalSubmitting(true);

    try {
      let success = false;
      if (editingRuleId) {
        success = await onUpdateRule?.(editingRuleId, payload);
      } else {
        success = await onCreateRule?.(payload);
      }

      if (success) {
        handleCancelEdit();
      }
    } catch (error) {
      console.error('Erro ao salvar regra de importação:', error);
    } finally {
      setLocalSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-5xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Regras de importação</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configure automações para ajustar notas fiscais importadas automaticamente.
            </p>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {view === 'list' && (
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {sortedRules.length === 0
                  ? 'Nenhuma regra configurada até o momento.'
                  : `${sortedRules.length} regra(s) configurada(s).`}
              </div>
              <button
                type="button"
                onClick={handleStartCreate}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold px-4 py-2 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nova regra
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-600 dark:text-gray-300">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando regras...
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {sortedRules.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center space-y-3">
                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                      Crie sua primeira regra de importação
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Defina condições para identificar notas específicas e automatize ações como ajustar vencimentos ou o tipo financeiro.
                    </p>
                    <button
                      type="button"
                      onClick={handleStartCreate}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold px-4 py-2 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Criar regra
                    </button>
                  </div>
                ) : (
                  sortedRules.map((rule) => {
                    const isBusy = effectiveBusyId !== null && String(effectiveBusyId) === String(rule.id);
                    const isActive = rule.is_active !== false;
                    return (
                      <div
                        key={rule.id ?? rule.nome}
                        className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 px-5 py-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{rule.nome}</h3>
                            {isActive ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-300">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Ativa
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 dark:text-amber-300">
                                <AlertTriangle className="w-3.5 h-3.5" /> Inativa
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                            <span>
                              Prioridade: <strong>{rule.prioridade ?? 0}</strong>
                            </span>
                            <span>
                              Condições: <strong>{Array.isArray(rule.conditions) ? rule.conditions.length : 0}</strong>
                            </span>
                            <span>
                              Ações: <strong>{Array.isArray(rule.actions) ? rule.actions.length : 0}</strong>
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-300 transition-colors"
                            onClick={() => handleStartEdit(rule)}
                            disabled={isBusy || effectiveSubmitting}
                          >
                            {isBusy && editingRuleId === rule.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Edit3 className="w-4 h-4" />
                            )}
                            Editar
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-300 transition-colors"
                            onClick={() => handleDuplicate(rule)}
                            disabled={isBusy || effectiveSubmitting}
                          >
                            {isBusy && localBusyId === rule.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                            Duplicar
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-lg border border-rose-200 text-rose-600 dark:border-rose-900/60 dark:text-rose-300 px-3 py-2 text-sm font-medium hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                            onClick={() => handleDelete(rule)}
                            disabled={isBusy || effectiveSubmitting}
                          >
                            {isBusy && localBusyId === rule.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            Excluir
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {view === 'edit' && (
          <form className="flex-1 overflow-y-auto custom-scrollbar" onSubmit={handleSubmit}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-300 transition-colors"
                disabled={effectiveSubmitting}
              >
                <ChevronLeft className="w-4 h-4" /> Voltar
              </button>
              <div className="flex items-center gap-2">
                {formError && (
                  <span className="text-sm text-rose-500 dark:text-rose-300">{formError}</span>
                )}
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-70"
                  disabled={effectiveSubmitting}
                >
                  {effectiveSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Salvar regra
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Nome da regra</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formState.nome}
                    onChange={(event) => setFormState((prev) => ({ ...prev, nome: event.target.value }))}
                    placeholder="Ex: NF de compras acima de R$ 10.000"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Prioridade</label>
                  <input
                    type="number"
                    className="input-field"
                    value={formState.prioridade}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, prioridade: event.target.value }))
                    }
                    min="0"
                    step="1"
                  />
                </div>
              </div>

              <div>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={formState.is_active}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, is_active: event.target.checked }))
                    }
                  />
                  Regra ativa
                </label>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Condições (AND)</h3>
                  <button
                    type="button"
                    onClick={() =>
                      setFormState((prev) => ({
                        ...prev,
                        conditions: [...prev.conditions, { ...DEFAULT_CONDITION }]
                      }))
                    }
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-300 transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Adicionar condição
                  </button>
                </div>

                <div className="space-y-3">
                  {formState.conditions.map((condition, index) => {
                    const useNumeric = shouldUseNumericInput(condition);
                    return (
                      <div
                        key={`condition-${index}`}
                        className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/40 p-4 space-y-3"
                      >
                        <div className="grid gap-3 md:grid-cols-[1fr,1fr,1fr,auto]">
                          <select
                            className="input-field"
                            value={condition.field}
                            onChange={(event) => {
                              const value = event.target.value;
                              setFormState((prev) => ({
                                ...prev,
                                conditions: prev.conditions.map((item, idx) =>
                                  idx === index
                                    ? { ...item, field: value }
                                    : item
                                )
                              }));
                            }}
                          >
                            {CONDITION_FIELDS.map((fieldOption) => (
                              <option key={fieldOption.value} value={fieldOption.value}>
                                {fieldOption.label}
                              </option>
                            ))}
                          </select>
                          <select
                            className="input-field"
                            value={condition.operator}
                            onChange={(event) => {
                              const value = event.target.value;
                              setFormState((prev) => ({
                                ...prev,
                                conditions: prev.conditions.map((item, idx) =>
                                  idx === index
                                    ? { ...item, operator: value }
                                    : item
                                )
                              }));
                            }}
                          >
                            {CONDITION_OPERATORS.map((operatorOption) => (
                              <option key={operatorOption.value} value={operatorOption.value}>
                                {operatorOption.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type={useNumeric ? 'number' : 'text'}
                            className="input-field"
                            value={condition.value}
                            onChange={(event) => {
                              const value = event.target.value;
                              setFormState((prev) => ({
                                ...prev,
                                conditions: prev.conditions.map((item, idx) =>
                                  idx === index
                                    ? { ...item, value }
                                    : item
                                )
                              }));
                            }}
                            placeholder="Valor da condição"
                          />
                          {formState.conditions.length > 1 && (
                            <button
                              type="button"
                              className="icon-button text-rose-500 hover:text-rose-600"
                              onClick={() =>
                                setFormState((prev) => ({
                                  ...prev,
                                  conditions: prev.conditions.filter((_, idx) => idx !== index)
                                }))
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Ações</h3>
                  <button
                    type="button"
                    onClick={() =>
                      setFormState((prev) => ({
                        ...prev,
                        actions: [...prev.actions, { ...DEFAULT_ACTION }]
                      }))
                    }
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-300 transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Adicionar ação
                  </button>
                </div>

                <div className="space-y-3">
                  {formState.actions.map((action, index) => (
                    <div
                      key={`action-${index}`}
                      className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/40 p-4 space-y-3"
                    >
                      <div className="grid gap-3 md:grid-cols-[1fr,2fr,auto] items-start md:items-center">
                        <select
                          className="input-field"
                          value={action.type}
                          onChange={(event) => {
                            const value = event.target.value;
                            let nextParams = {};
                            if (value === 'set_due_date_offset') {
                              nextParams = { days: 0, base: 'auto' };
                            } else if (value === 'set_financial_type') {
                              nextParams = { financialType: 'automatico' };
                            } else if (value === 'set_gera_financeiro') {
                              nextParams = { enabled: true };
                            }
                            setFormState((prev) => ({
                              ...prev,
                              actions: prev.actions.map((item, idx) =>
                                idx === index
                                  ? { ...item, type: value, params: nextParams }
                                  : item
                              )
                            }));
                          }}
                        >
                          {ACTION_TYPES.map((actionOption) => (
                            <option key={actionOption.value} value={actionOption.value}>
                              {actionOption.label}
                            </option>
                          ))}
                        </select>

                        <div className="space-y-2 md:space-y-0 md:flex md:items-center md:gap-2">
                          {action.type === 'set_due_date_offset' && (
                            <>
                              <input
                                type="number"
                                className="input-field"
                                value={action.params?.days ?? 0}
                                onChange={(event) => {
                                  const value = Number(event.target.value) || 0;
                                  setFormState((prev) => ({
                                    ...prev,
                                    actions: prev.actions.map((item, idx) =>
                                      idx === index
                                        ? { ...item, params: { ...item.params, days: value } }
                                        : item
                                    )
                                  }));
                                }}
                                placeholder="Dias para ajustar vencimento"
                              />
                              <select
                                className="input-field"
                                value={action.params?.base ?? 'auto'}
                                onChange={(event) => {
                                  const value = event.target.value || 'auto';
                                  setFormState((prev) => ({
                                    ...prev,
                                    actions: prev.actions.map((item, idx) =>
                                      idx === index
                                        ? { ...item, params: { ...item.params, base: value } }
                                        : item
                                    )
                                  }));
                                }}
                              >
                                {DUE_DATE_BASE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </>
                          )}

                          {action.type === 'set_financial_type' && (
                            <select
                              className="input-field"
                              value={action.params?.financialType ?? 'automatico'}
                              onChange={(event) => {
                                const value = event.target.value;
                                setFormState((prev) => ({
                                  ...prev,
                                  actions: prev.actions.map((item, idx) =>
                                    idx === index
                                      ? { ...item, params: { ...item.params, financialType: value } }
                                      : item
                                  )
                                }));
                              }}
                            >
                              {FINANCIAL_TYPES.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          )}

                          {action.type === 'set_gera_financeiro' && (
                            <select
                              className="input-field"
                              value={action.params?.enabled ? 'ativar' : 'desativar'}
                              onChange={(event) => {
                                const value = event.target.value === 'ativar';
                                setFormState((prev) => ({
                                  ...prev,
                                  actions: prev.actions.map((item, idx) =>
                                    idx === index
                                      ? { ...item, params: { ...item.params, enabled: value } }
                                      : item
                                  )
                                }));
                              }}
                            >
                              <option value="ativar">Ativar gera_financeiro</option>
                              <option value="desativar">Desativar gera_financeiro</option>
                            </select>
                          )}
                        </div>

                        {formState.actions.length > 1 && (
                          <button
                            type="button"
                            className="icon-button text-rose-500 hover:text-rose-600"
                            onClick={() =>
                              setFormState((prev) => ({
                                ...prev,
                                actions: prev.actions.filter((_, idx) => idx !== index)
                              }))
                            }
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
